import { ScriptService } from 'services/script/script.service'
import { logger } from 'utils/system/logger'

enum PlaybackState {
    UNSTARTED = 'unstarted',
    START = 'start'
}

enum AdState {
    PREROLL = 'preroll',
    MIDROLL = 'midroll',
    POSTROLL = 'postroll'
}

export class ComscoreAnalytics {
    /**
     * Singleton instance so we only initialize once
     */

    private static instance: ComscoreAnalytics
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private analytics: any
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private sa: any
    private state: PlaybackState
    private adState: AdState
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private cm: any

    /**
     * Initializes ComscoreAnalytics
     */
    constructor() {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this.setUpComscore()
    }

    /**
     * Get Singleton instance
     */
    public static getInstance() {
        if (!ComscoreAnalytics.instance) {
            ComscoreAnalytics.instance = new ComscoreAnalytics()
        }
        return ComscoreAnalytics.instance
    }

    /**
     * Set up the Comscore analytics services. Implementation details can be found here:
     * https://postmediadigital.atlassian.net/browse/CHEET-4793
     */
    private async setUpComscore(): Promise<void> {
        await ScriptService.getInstance()
            .loadScript({
                name: 'comscore',
                src: 'https://storage.googleapis.com/pmd-dev-northamerica-northeast1-asset-analytics-pub/js/comscore.js',
                loaded: false
            })
            .then(() => {
                logger('log', 'Comscore Script Loaded')
            })
            .catch((error: Error) =>
                logger('error', `Comscore script not loaded : ${error}`)
            )

        if (window.ns_ !== undefined) {
            this.analytics = window.ns_.analytics
            this.analytics.PlatformApi.setPlatformAPI(
                this.analytics.PlatformApi.PlatformApis.WebBrowser
            )
            logger('log', 'Comscore Platform Set')
            const myPublisherConfig =
                new this.analytics.configuration.PublisherConfiguration({
                    publisherId: '10276888'
                })
            this.analytics.configuration.addClient(myPublisherConfig)
            this.analytics.configuration.enableFirstPartyCookie()
            this.analytics.configuration.setUsagePropertiesAutoUpdateMode(
                this.analytics.configuration.UsagePropertiesAutoUpdateMode
                    .FOREGROUND_AND_BACKGROUND
            )
            logger('log', 'Comscore Configured')
            this.analytics.start()
            logger('log', 'Comscore Started')
        }

        this.setUpStreamingAnalytics()
        this.state = PlaybackState.UNSTARTED
    }

    /**
     * Required for setting up Comscore analytics
     */
    public setUpStreamingAnalytics(): void {
        if (window.ns_ !== undefined) {
            this.sa = new this.analytics.StreamingAnalytics()
            this.sa.setMediaPlayerName('jw-player')
            // this.sa.setMediaPlayerVersion(version)
        }
    }

    /**
     * Playback sessions are created for each new content video
     */
    public createPlaybackSession(): void {
        this.sa.createPlaybackSession()
        logger('log', 'Comscore Playback Session created')
        this.state = PlaybackState.START
        this.adState = AdState.PREROLL
    }

    /**
     * Sets the content metadata based on data from the video player and the current site URL
     * @param videoId The unique ID of the video
     * @param videoTitle The title of the video
     * @param stationTitle The location the video is being played from, e.g. NPNews or TSunPolitics
     * @param publisherName The property that owns the video, e.g. nationalpost or financialpost
     * @param duration The duration of the video in milliseconds
     */
    public specifyContentMetadata(
        videoId: string,
        videoTitle: string,
        stationTitle: string,
        publisherName: string,
        duration: number
    ): void {
        this.cm = new this.analytics.StreamingAnalytics.ContentMetadata()
        // eslint-disable-next-line no-magic-numbers
        if (duration > 600000)
            this.cm.setMediaType(
                this.analytics.StreamingAnalytics.ContentMetadata.ContentType
                    .LONG_FORM_ON_DEMAND
            )
        else
            this.cm.setMediaType(
                this.analytics.StreamingAnalytics.ContentMetadata.ContentType
                    .SHORT_FORM_ON_DEMAND
            )
        this.cm.setUniqueId(videoId)
        this.cm.setLength(duration)
        // get property name from URL
        const siteName = window.location.hostname.replace(
            /(?:o\.|www\.)?(.*?)\.(?:on\.)?(?:com|ca)/,
            '$1'
        )
        this.cm.setDictionaryClassificationC3(siteName)
        this.cm.setDictionaryClassificationC4('*null')
        this.cm.setDictionaryClassificationC6('*null')
        this.cm.setStationTitle(stationTitle)
        this.cm.setPublisherName(publisherName)
        this.cm.setProgramTitle(videoTitle)
        this.cm.setGenreName('*null')

        logger(
            'debug',
            'specifyContentMetadata called with',
            `videoId: ${videoId}`,
            `videoTitle: ${videoTitle}`,
            `stationTitle: ${stationTitle}`,
            `publisherName: ${publisherName}`,
            `C3 (site name): ${siteName}`,
            `duration: ${duration}`
        )
    }

    public setContentMetadata(): void {
        this.sa.setMetadata(this.cm)
    }

    /**
     * Sets advertising metadata based on when in the video the advertisement is being shown,
     * the length of the ad, and the content the ad is associated with.
     * @param duration The duration of the ad in milliseconds
     */
    public setAdvertisingMetadata(duration: number): void {
        const am = new this.analytics.StreamingAnalytics.AdvertisementMetadata()
        if (this.adState === AdState.PREROLL) {
            am.setMediaType(
                this.analytics.StreamingAnalytics.AdvertisementMetadata
                    .AdvertisementType.ON_DEMAND_PRE_ROLL
            )
        } else if (this.adState === AdState.MIDROLL) {
            am.setMediaType(
                this.analytics.StreamingAnalytics.AdvertisementMetadata
                    .AdvertisementType.ON_DEMAND_MID_ROLL
            )
        } else if (this.adState === AdState.POSTROLL) {
            am.setMediaType(
                this.analytics.StreamingAnalytics.AdvertisementMetadata
                    .AdvertisementType.ON_DEMAND_POST_ROLL
            )
        }
        am.setLength(duration)
        am.setRelatedContentMetadata(this.cm)

        logger(
            'debug',
            'specifyAdvertisingMetadata called with',
            `roll type: ${this.adState}`,
            `duration: ${duration}`
        )

        this.sa.setMetadata(am)
    }

    public notifyBufferStart(): void {
        this.sa.notifyBufferStart()
    }

    public notifyBufferStop(): void {
        this.sa.notifyBufferStop()
    }

    public notifyPlay(): void {
        this.sa.notifyPlay()
    }

    public notifyPause(): void {
        this.sa.notifyPause()
    }

    /**
     * After a video ends the player is in an unstarted state. The player can enter an unstarted
     * state either when the current video ends, or another video in the playlist is selected for playback.
     */
    public notifyEnd(): void {
        this.sa.notifyEnd()
        this.state = PlaybackState.UNSTARTED
    }

    public notifySeekStart(): void {
        this.sa.notifySeekStart()
    }

    /**
     * After a new playlist item is loaded the player is in an unstarted state.
     */
    public newPlaylistItem(): void {
        this.state = PlaybackState.UNSTARTED
    }

    /**
     * Checks state to see if a new video is starting and if so, creates new PlaybackSession and returns true
     * @returns A boolean, whether this beforePlay event represents a new video starting
     */
    public beforePlay(): boolean {
        if (this.state === PlaybackState.UNSTARTED) {
            this.createPlaybackSession()
            return true
        }
        return false
    }

    /**
     * Preroll ads are completed by the time the firstFrame event fires
     */
    public firstFrame(): void {
        this.adState = AdState.MIDROLL
        this.setContentMetadata()
    }

    /**
     * Postroll ads follow the beforeComplete event
     */
    public beforeComplete(): void {
        this.adState = AdState.POSTROLL
    }

    /**
     *
     * @param position The time from the start of the video, in milliseconds
     */
    public startFromPosition(position: number): void {
        this.sa.startFromPosition(position.toFixed(0))
    }

    public notifyChangePlaybackRate(rate: number): void {
        this.sa.notifyChangePlaybackRate(rate)
    }
}
